import React, { useState, useEffect } from "react";
import MCQSlide from "./MCQSlide";
import TaskSlide from "./TaskSlide";
import SlideRenderer from "./SlideRenderer";

export default function QuestionsPanel({
  questions,
  onLoadCode,
  setLang,
}) {
  const [index, setIndex] = useState(0);
  const [sel, setSel] = useState(null);
  const [message, setMessage] = useState(null);
  const [solved, setSolved] = useState({});

  const slide = questions[index];

  // Reset selection when changing slides
  useEffect(() => {
    setSel(null);
    setMessage(null);
  }, [index]);

  // preload starter code for task slides
  useEffect(() => {
    if (!slide) return;

    if (slide.type === "task" && slide.task?.starter) {
      const starter = slide.task.starter;

      if (typeof starter === "object") {
        const html = starter.html ?? "";
        const css = starter.css ?? "";
        const js = starter.js ?? "";
        onLoadCode(html, css, js);
        
        // Auto-switch to the appropriate editor based on content or validator
        const validator = slide.task.validator;
        if (setLang) {
          if (slide.task.lang) {
            // If lang is explicitly set, use it
            setLang(slide.task.lang);
          } else if (validator) {
            // Auto-detect based on validator pattern
            const pattern = validator.args;
            if (typeof pattern === "string") {
              if (pattern.includes("{") || 
                  pattern.includes("color:") || 
                  pattern.includes("background") ||
                  pattern.includes("font-") ||
                  pattern.includes("margin") ||
                  pattern.includes("padding") ||
                  pattern.includes("border") ||
                  pattern.match(/\w+\s*\{/) ||
                  pattern.match(/:\s*\w+/) ||
                  pattern.includes("px") ||
                  pattern.includes("em") ||
                  pattern.includes("rem")) {
                setLang("css");
              } else if (pattern.includes("function") ||
                        pattern.includes("console.log") ||
                        pattern.includes("document.") ||
                        pattern.includes("window.") ||
                        pattern.includes("addEventListener") ||
                        pattern.includes("querySelector") ||
                        pattern.includes("var ") ||
                        pattern.includes("let ") ||
                        pattern.includes("const ") ||
                        pattern.includes("=>") ||
                        pattern.includes("alert(")) {
                setLang("js");
              } else {
                setLang("html");
              }
            } else {
              // Default based on which code has content
              if (css.trim()) setLang("css");
              else if (js.trim()) setLang("js");
              else setLang("html");
            }
          } else {
            // Default based on which code has content
            if (css.trim()) setLang("css");
            else if (js.trim()) setLang("js");
            else setLang("html");
          }
        }
      } else if (typeof starter === "string") {
        // legacy case: single string starter
        let html = "", css = "", js = "";
        const lang = slide.task?.lang ?? "html";
        if (lang === "html") html = starter;
        else if (lang === "css") css = starter;
        else js = starter;
        onLoadCode(html, css, js);
        
        // Switch to the appropriate editor
        if (setLang) {
          setLang(lang);
        }
      }

      // Don't set solved to false immediately - preserve existing state
      if (!(index in solved)) {
        setSolved((s) => ({ ...s, [index]: false }));
      }
    }
  }, [index, slide, setLang]);

  const clickNext = () => {
    if (canNext() && index < questions.length - 1) {
      setIndex((i) => i + 1);
      setMessage(null);
      setSel(null);
    }
  };

  const clickPrevious = () => {
    if (index > 0) {
      setIndex((i) => i - 1);
      setMessage(null);
      setSel(null);
    }
  };

  const canNext = () => {
    if (!slide) return false;
    if (slide.type === "task") return !!solved[index];
    if (slide.type === "mcq") return !!solved[index];
    return true; // assignment slides can always proceed
  };

  const isLastSlide = index === questions.length - 1;

  return (
    <div className="space-y-4">
      {/* Progress indicator */}
      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
        <div className="text-sm text-gray-600">
          Question {index + 1} of {questions.length}
        </div>
        <div className="flex space-x-1">
          {questions.map((_, i) => (
            <div
              key={i}
              className={`w-3 h-3 rounded-full ${
                i < index
                  ? "bg-green-500"
                  : i === index
                  ? "bg-blue-500"
                  : "bg-gray-300"
              }`}
            />
          ))}
        </div>
      </div>

      {slide?.type === "assignment" && (
        <div className="p-4 border rounded bg-white shadow-sm">
          <h3 className="font-bold text-lg mb-2">{slide.title}</h3>
          {slide.html ? (
            <SlideRenderer slide={{ html: slide.html }} />
          ) : (
            <p className="text-gray-700">{slide.description}</p>
          )}
        </div>
      )}

      {slide?.type === "mcq" && (
        <MCQSlide 
          key={slide.id} 
          slide={slide} 
          sel={sel} 
          setSel={setSel}
          onCorrect={() => setSolved((s) => ({ ...s, [index]: true }))}
        />
      )}

      {slide?.type === "task" && (
        <TaskSlide
          key={slide.id}
          slide={slide}
          onCorrect={() => setSolved((s) => ({ ...s, [index]: true }))}
          onIncorrect={() => setMessage("Try again! 🤔")}
          message={message}
          setMessage={setMessage}
        />
      )}

      <div className="flex justify-between items-center mt-4">
        <button
          className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300"
          onClick={clickPrevious}
          disabled={index === 0}
        >
          ← Previous
        </button>
        
        <div className="text-sm text-gray-500">
          {!canNext() && !isLastSlide && (
            <span>Complete this step to continue! ✨</span>
          )}
          {isLastSlide && canNext() && (
            <span className="text-green-600">🎉 All done! Great job!</span>
          )}
        </div>

        <button
          className="px-4 py-2 bg-blue-500 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-600"
          onClick={clickNext}
          disabled={!canNext() || isLastSlide}
        >
          {isLastSlide ? "Finished!" : "Next →"}
        </button>
      </div>
    </div>
  );
}